import { useMemo, useState } from "react";
import SectionHeading from "./SectionHeading";
import AnimeCard from "@/components/feature/AnimeCard";
import { classicAnime, genres, hotAnime, lateNightAnime } from "@/mocks/anime";

const allAnime = [...hotAnime, ...lateNightAnime, ...classicAnime];

export default function GenreFilterSection() {
  const [genre, setGenre] = useState("all");
  const [keyword, setKeyword] = useState("");

  const list = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    return allAnime.filter((item) => {
      const matchGenre = genre === "all" || item.genreKey === genre;
      const matchKeyword =
        key.length === 0 ||
        item.title.toLowerCase().includes(key) ||
        item.subtitle.toLowerCase().includes(key) ||
        item.genre.toLowerCase().includes(key) ||
        item.summary.toLowerCase().includes(key) ||
        item.tags.some((tag) => tag.toLowerCase().includes(key));
      return matchGenre && matchKeyword;
    });
  }, [genre, keyword]);

  return (
    <section id="genre" className="w-full bg-background-50 px-4 py-14 md:px-8 md:py-20">
      <SectionHeading
        eyebrow="在本页即时筛选"
        title="类型筛选与关键词检索"
        desc="把热门、深夜与经典三个片库合在一起检索：先点类型标签缩小范围，再输入片名或题材关键词，结果会立刻显示在下方。"
        align="center"
      />

      <div className="mt-8 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
        <div className="flex flex-wrap gap-2">
          {genres.map((g) => (
            <button
              key={g.key}
              type="button"
              onClick={() => setGenre(g.key)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors cursor-pointer ${
                genre === g.key
                  ? "bg-secondary-500 text-background-50"
                  : "bg-background-50 text-foreground-600 hover:text-secondary-700"
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5">
            <i className="ri-search-line text-foreground-400" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              placeholder="输入片名、题材或标签关键词，例如「悬疑」「音乐」「太空」"
              className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
            />
            {keyword && (
              <button
                type="button"
                aria-label="清空关键词"
                onClick={() => setKeyword("")}
                className="shrink-0 text-foreground-400 transition-colors hover:text-primary-500 cursor-pointer"
              >
                <i className="ri-close-circle-fill" />
              </button>
            )}
          </div>
          <span className="shrink-0 text-sm text-foreground-600">
            命中 <strong className="font-bold text-primary-600">{list.length}</strong> 部作品
          </span>
        </div>
      </div>

      {list.length > 0 ? (
        <div className="mt-8 grid grid-cols-2 gap-4 md:gap-5 lg:grid-cols-4 xl:grid-cols-5">
          {list.map((item) => (
            <AnimeCard key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <div className="mt-8 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 py-14 text-center">
          <i className="ri-emotion-sad-line text-4xl text-foreground-300" />
          <p className="mt-3 font-heading text-xl text-foreground-800">没有匹配到作品</p>
          <p className="mt-1 text-sm text-foreground-500">
            换个类型标签，或试试更短的关键词
          </p>
          <button
            type="button"
            onClick={() => {
              setGenre("all");
              setKeyword("");
            }}
            className="mt-4 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            重置筛选
          </button>
        </div>
      )}
    </section>
  );
}